/**
 * Tiny "Ad-free with VIP" promo chip that fades in for ~6s whenever
 * an ad fires for a non-VIP, non-PTL listener — exactly the moment
 * the listener feels the friction of ads and is most likely to
 * convert. Triggered by the `ovd:radio-ad-fired` window event from
 * SyncedRadioPlayer / RadioAds.js.
 *
 * The chip surfaces a *dual* upgrade path:
 *   • VIP — fully ad-free experience
 *   • Paid-To-Listen — bypasses audio ads + lets them earn cash for
 *     listening (highest LTV upsell)
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Headphones, Sparkles, X } from 'lucide-react';

const HIDE_AFTER_MS = 6000;

export function AdFreeUpgradeChip() {
  const [visible, setVisible] = useState(false);
  const [hideTimer, setHideTimer] = useState(null);

  useEffect(() => {
    const onAdFired = (e) => {
      // Skip if the listener is already audio-ad-free (VIP or PTL).
      if (e.detail?.audio_ad_free) return;
      setVisible(true);
      if (hideTimer) clearTimeout(hideTimer);
      const t = setTimeout(() => setVisible(false), HIDE_AFTER_MS);
      setHideTimer(t);
    };
    window.addEventListener('ovd:radio-ad-fired', onAdFired);
    return () => {
      window.removeEventListener('ovd:radio-ad-fired', onAdFired);
      if (hideTimer) clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed top-20 left-1/2 -translate-x-1/2 z-[10000] max-w-md w-[92%] sm:w-auto"
      data-testid="ad-free-upgrade-chip"
      role="status"
      style={{ animation: 'ovd-chip-fade 320ms ease-out both' }}
    >
      <style>{`
        @keyframes ovd-chip-fade {
          from { opacity: 0; transform: translate(-50%, -8px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
      <div className="rounded-2xl border-2 border-[#FFDA33]/50 bg-[#0a0a0a]/95 backdrop-blur-md shadow-[0_0_40px_rgba(255,218,51,0.35)] overflow-hidden">
        <div className="flex items-start gap-3 p-3 pr-2">
          <div className="w-9 h-9 rounded-full bg-[#FFDA33]/15 flex items-center justify-center shrink-0">
            <Sparkles size={16} className="text-[#FFDA33]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono uppercase tracking-widest text-[10px] text-[#FFDA33]">
              Skip ads forever
            </p>
            <p className="text-white text-sm font-bold mt-0.5">
              Two ways to never hear an ad again:
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Link
                to="/vip"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gradient-to-r from-[#FFDA33] to-[#FFE97A] text-black font-mono uppercase tracking-widest text-[10px] font-bold hover:brightness-110 transition-all"
                data-testid="ad-chip-vip-cta"
                onClick={() => setVisible(false)}
              >
                <Crown size={12} /> Go VIP
              </Link>
              <Link
                to="/paid-to-listen/info"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gradient-to-r from-[#00ff88] to-[#00cc6a] text-black font-mono uppercase tracking-widest text-[10px] font-bold hover:brightness-110 transition-all"
                data-testid="ad-chip-ptl-cta"
                onClick={() => setVisible(false)}
              >
                <Headphones size={12} /> Join Paid-To-Listen
              </Link>
            </div>
          </div>
          <button
            onClick={() => setVisible(false)}
            className="text-[#666] hover:text-white p-1 rounded-full hover:bg-white/10"
            aria-label="Dismiss"
            data-testid="ad-chip-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdFreeUpgradeChip;
